import {
  Building2,
  Home,
  ArrowRightLeft,
  Plane,
  Car,
  TrendingUp,
  UtensilsCrossed,
  type LucideIcon,
} from 'lucide-react';

import { useEvent } from '../contexts/EventContext';
import { ExternalLinkButton } from '../components/ExternalLinkButton';
import type { TransportIcon } from '../data/types';

const TRANSPORT_ICONS: Record<TransportIcon, LucideIcon> = {
  plane: Plane,
  car: Car,
  transit: TrendingUp,
};

function openMap(query: string) {
  const mapsUrl = `https://maps.google.com/?q=${encodeURIComponent(query)}`;
  window.open(mapsUrl, '_blank');
}

export function LogisticsPage() {
  const { logistics } = useEvent();
  const {
    venue,
    dinner,
    hotels,
    transportation,
    restaurants,
    wifi,
    attire,
    notes = [],
  } = logistics;

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl lg:text-4xl dark:text-white">
          Logistics & Travel
        </h1>
        <p className="mt-2 text-base text-gray-600 sm:text-lg dark:text-gray-300">
          {logistics.intro}
        </p>
      </div>

      <div className="space-y-8">
        {/* Important Information */}
        {(attire || wifi || notes.length > 0) && (
          <div className="bg-secondary-50 dark:bg-secondary-900/20 mb-8 rounded-lg p-4 sm:p-6">
            <h3 className="text-secondary-900 dark:text-secondary-300 text-base font-semibold sm:text-lg">
              Important Information
            </h3>
            <ul className="text-secondary-800 dark:text-secondary-300 mt-3 list-inside list-disc space-y-2 text-sm sm:text-base">
              {attire && <li>{attire}</li>}
              {notes.map((note, i) => (
                <li key={i}>{note}</li>
              ))}
              {wifi && (
                <li>
                  WiFi will be available throughout the venue
                  <div className="mt-2 ml-5 space-y-1 rounded-md bg-white/50 p-3 dark:bg-gray-800/50">
                    <p className="font-medium">Network: {wifi.network}</p>
                    <p className="font-medium">Password: {wifi.password}</p>
                  </div>
                </li>
              )}
            </ul>
          </div>
        )}

        {/* Venue Information */}
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-6 dark:border-gray-700 dark:bg-gray-800">
          <div className="flex items-start gap-3">
            <div className="bg-primary-100 dark:bg-primary-900 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg sm:h-12 sm:w-12">
              <Building2 className="text-primary-600 dark:text-primary-400 h-5 w-5 sm:h-6 sm:w-6" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-xl font-semibold text-gray-900 sm:text-2xl dark:text-white">
                Venue
              </h2>
            </div>
          </div>
          <div className="mt-3 space-y-2 text-sm text-gray-700 sm:text-base dark:text-gray-300">
            <p className="font-medium">{venue.name}</p>
            {venue.addressLines.map((line, i) => (
              <p key={i}>{line}</p>
            ))}
            {venue.note && (
              <p className="mt-3 text-xs text-gray-600 sm:text-sm dark:text-gray-400">
                {venue.note}
              </p>
            )}
          </div>
          <button
            onClick={() => openMap(venue.mapQuery)}
            className="bg-primary-600 hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-600 mt-4 w-full rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors sm:w-auto"
          >
            View on Map
          </button>
          {venue.image && (
            <div className="mt-4">
              <img
                src={venue.image}
                alt={`${venue.name} meeting room`}
                className="w-full rounded-lg shadow-md"
              />
            </div>
          )}
        </div>

        {/* Dinner */}
        {dinner && (
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-6 dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-start gap-3">
              <div className="bg-secondary-100 dark:bg-secondary-900 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg sm:h-12 sm:w-12">
                <UtensilsCrossed className="text-secondary-600 dark:text-secondary-400 h-5 w-5 sm:h-6 sm:w-6" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-xl font-semibold text-gray-900 sm:text-2xl dark:text-white">
                  Dinner
                </h2>
              </div>
            </div>
            <div className="mt-3 space-y-2 text-sm text-gray-700 sm:text-base dark:text-gray-300">
              <p className="font-medium">{dinner.name}</p>
              {dinner.addressLines.map((line, i) => (
                <p key={i}>{line}</p>
              ))}
              {dinner.note && (
                <p className="mt-3 text-xs text-gray-600 sm:text-sm dark:text-gray-400">
                  {dinner.note}
                </p>
              )}
            </div>
            <button
              onClick={() => openMap(dinner.mapQuery)}
              className="bg-primary-600 hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-600 mt-4 w-full rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors sm:w-auto"
            >
              View on Map
            </button>
            {dinner.images?.map((image, i) => (
              <div key={i} className="mt-4">
                <img
                  src={image}
                  alt={`${dinner.name} ${i + 1}`}
                  className="w-full rounded-lg shadow-md"
                />
              </div>
            ))}
          </div>
        )}

        {/* Accommodations */}
        {hotels.length > 0 && (
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-6 dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-start gap-3">
              <div className="bg-secondary-100 dark:bg-secondary-900 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg sm:h-12 sm:w-12">
                <Home className="text-secondary-600 dark:text-secondary-400 h-5 w-5 sm:h-6 sm:w-6" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-xl font-semibold text-gray-900 sm:text-2xl dark:text-white">
                  Recommended Hotels
                </h2>
              </div>
            </div>
            <div className="mt-4 space-y-3 sm:space-y-4">
              {hotels.map((hotel, i) => (
                <div
                  key={i}
                  className="rounded-lg bg-gray-50 p-3 sm:p-4 dark:bg-gray-700"
                >
                  <h3 className="text-sm font-semibold text-gray-900 sm:text-base dark:text-white">
                    {hotel.name}
                  </h3>
                  {hotel.description && (
                    <p className="text-xs text-gray-600 sm:text-sm dark:text-gray-400">
                      {hotel.description}
                    </p>
                  )}
                  {hotel.rate && (
                    <p className="mt-2 text-xs text-gray-700 sm:text-sm dark:text-gray-300">
                      {hotel.rate}
                    </p>
                  )}
                  {hotel.bookingUrl && (
                    <div>
                      <ExternalLinkButton
                        link={{ label: 'Book Now', url: hotel.bookingUrl }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Transportation */}
        {transportation.length > 0 && (
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-6 dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-start gap-3">
              <div className="bg-accent-100 dark:bg-accent-900 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg sm:h-12 sm:w-12">
                <ArrowRightLeft className="text-accent-600 dark:text-accent-400 h-5 w-5 sm:h-6 sm:w-6" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-xl font-semibold text-gray-900 sm:text-2xl dark:text-white">
                  Getting to Miami
                </h2>
              </div>
            </div>
            <div className="mt-4 space-y-3 sm:space-y-4">
              {transportation.map((item, i) => {
                const Icon = TRANSPORT_ICONS[item.icon];
                return (
                  <div
                    key={i}
                    className="rounded-lg bg-gray-50 p-3 sm:p-4 dark:bg-gray-700"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center">
                        <Icon className="text-accent-600 dark:text-accent-400 h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-sm font-semibold text-gray-900 sm:text-base dark:text-white">
                          {item.title}
                        </h3>
                        {item.lines.map((line, li) => (
                          <p
                            key={li}
                            className="mt-1 text-xs text-gray-700 sm:text-sm dark:text-gray-300"
                          >
                            {line}
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Dining & Restaurants */}
        {restaurants.length > 0 && (
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-6 dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-start gap-3">
              <div className="bg-secondary-100 dark:bg-secondary-900 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg sm:h-12 sm:w-12">
                <UtensilsCrossed className="text-secondary-600 dark:text-secondary-400 h-5 w-5 sm:h-6 sm:w-6" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-xl font-semibold text-gray-900 sm:text-2xl dark:text-white">
                  Nearby Dining Options
                </h2>
              </div>
            </div>
            <div className="mt-4 space-y-3 sm:space-y-4">
              {restaurants.map((r, i) => (
                <div
                  key={i}
                  className="rounded-lg bg-gray-50 p-3 sm:p-4 dark:bg-gray-700"
                >
                  <h3 className="text-sm font-semibold text-gray-900 sm:text-base dark:text-white">
                    {r.name}
                  </h3>
                  {r.cuisine && (
                    <p className="mt-1 text-xs text-gray-600 sm:text-sm dark:text-gray-400">
                      {r.cuisine}
                    </p>
                  )}
                  {r.description && (
                    <p className="mt-1 text-xs text-gray-700 dark:text-gray-300">
                      {r.description}
                    </p>
                  )}
                  {r.address && (
                    <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                      {r.address}
                    </p>
                  )}
                  {r.reservationUrl && (
                    <a
                      href={r.reservationUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 mt-2 inline-block text-xs font-medium"
                    >
                      Reserve a table →
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
